import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', to: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2023', title: '品牌创立', desc: '蜜桃视频正式上线，致力于打造甜蜜短视频社区' },
  { year: '2024', title: '用户突破100万', desc: '凭借优质内容和良好口碑，注册用户快速增长' },
  { year: '2025', title: '创作者生态完善', desc: '推出创作者激励计划，签约超过50万创作者' },
  { year: '2026', title: '用户突破1000万', desc: '成为国内领先的甜蜜生活方式短视频平台' },
];

const reviews = [
  {
    name: '小甜甜',
    text: '蜜桃视频真的是我每天必刷的APP！内容质量很高，没有那些低俗内容，感觉很温暖很治愈。特别喜欢美食和旅行分类~',
    rating: 5,
  },
  {
    name: '旅行达人阿明',
    text: '作为一个旅行博主，蜜桃视频给了我一个很好的展示平台。这里的用户都很友善，评论区的氛围特别好，不像其他平台那么多喷子。',
    rating: 5,
  },
  {
    name: '猫咪控小美',
    text: '萌宠分类简直是我的快乐源泉！每天看各种猫猫狗狗的视频，心情都会变好。推荐给了很多朋友，大家都说好用！',
    rating: 5,
  },
  {
    name: '深夜食堂小王',
    text: '在蜜桃视频上分享我的美食制作过程，没想到收获了这么多粉丝。平台的创作工具也很好用，剪辑方便，推荐算法也很精准。',
    rating: 4,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u43nl3pcjqs2fcslm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '蜜桃视频',
    url: 'https://sweetvid.github.io/',
    description: '甜蜜短视频平台，发现精彩生活',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      email: 'support@mitaovideo.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于蜜桃视频',
        description: '蜜桃视频品牌介绍、用户口碑与联系方式',
        url: 'https://sweetvid.github.io/about',
      })}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Hero */}
          <section className="relative w-full h-[200px] md:h-[300px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20modern%20office%20interior%20with%20soft%20peach%20and%20cream%20color%20palette%2C%20minimal%20design%2C%20natural%20lighting%2C%20creative%20workspace%2C%20clean%20aesthetic%2C%20no%20people%2C%20corporate%20photography&width=1800&height=600&seq=about-hero&orientation=landscape"
              alt="蜜桃视频品牌介绍"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/40"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h1 className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                关于蜜桃视频
              </h1>
              <p className="text-sm md:text-base text-white/70 mt-2 max-w-lg">
                让每一个生活瞬间都充满甜蜜与温暖
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  品牌故事
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  蜜桃视频创立于2023年，我们的初衷很简单——打造一个<strong>温暖、治愈、积极向上</strong>的短视频社区。在这个信息爆炸的时代，我们希望为用户提供一个可以放松心情、发现美好的空间。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  平台名称"蜜桃"象征着甜蜜与美好，这正是我们希望传递给每一位用户的感受。无论是分享一道美食、记录一段旅程、还是捕捉宠物的可爱瞬间，蜜桃视频都致力于让每一个生活瞬间都充满甜蜜与温暖。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  如今，蜜桃视频已拥有超过<strong>1000万活跃用户</strong>和<strong>50万创作者</strong>，涵盖美食、旅行、萌宠、情感、生活、美妆、音乐、运动等八大热门领域，每天有超过500万条优质内容在平台上分享和传播。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Creative%20team%20collaborating%20in%20a%20warm%20modern%20office%2C%20minimal%20design%2C%20natural%20lighting%2C%20friendly%20atmosphere%2C%20peach%20and%20cream%20color%20palette%2C%20corporate%20lifestyle%20photography%2C%20soft%20focus&width=800&height=600&seq=about-story&orientation=landscape"
                  alt="蜜桃视频团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
                发展历程
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {milestones.map((m, i) => (
                  <div key={i} className="text-center bg-background-50 rounded-lg p-6">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold text-lg mx-auto mb-3">
                      {m.year}
                    </div>
                    <h3 className="font-semibold text-foreground-900 mb-2">{m.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* User Reviews */}
          <section className="max-w-5xl mx-auto px-4 md:px-6 py-14 md:py-20">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10" style={{ fontFamily: 'var(--font-heading)' }}>
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reviews.map((review, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <div key={starIdx} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${starIdx < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                  <p className="text-xs text-foreground-500 font-medium">— {review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                有任何问题或建议？我们很乐意听取你的意见
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mx-auto mb-3">
                    <i className="ri-mail-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">support@mitaovideo.com</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                    <i className="ri-phone-line text-accent-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-500">400-888-6666</p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 mx-auto mb-3">
                    <i className="ri-wechat-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-medium text-foreground-900 text-sm mb-1">微信公众号</h3>
                  <p className="text-xs text-foreground-500">蜜桃视频</p>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-lg p-6 md:p-8">
                <h3 className="font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-name" className="text-xs font-medium text-foreground-700">姓名 <span className="text-primary-500">*</span></label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className="text-xs font-medium text-foreground-700">邮箱 <span className="text-primary-500">*</span></label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-700">主题</label>
                    <input
                      id="contact-subject"
                      name="subject"
                      type="text"
                      className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-message" className="text-xs font-medium text-foreground-700">消息内容 <span className="text-primary-500">*</span></label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                      placeholder="请输入你想说的话（500字以内）"
                    ></textarea>
                  </div>

                  {formError && (
                    <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-md">{formError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap transition-all duration-200 cursor-pointer disabled:opacity-50 w-fit"
                  >
                    {isSubmitting ? '提交中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded-md">
                      消息已发送！我们会尽快回复你。
                    </p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}